import React, { FC, useState, useMemo } from 'react';
import {
  ForgeButton,
  ForgeIcon,
  ForgeIconButton,
  ForgeLabelValue,
  ForgePopover,
  ForgeSelect,
  ForgeSlider,
} from '@tylertech/forge-react';

import './index.scss';
import {
  PDF_CONFIGURATION_COLUMN_WIDTH,
  PDF_CONFIGURATION_ORIENTATION,
  PDF_CONFIGURATION_PAGE_BREAK,
  PDF_CONFIGURATION_PAGE_SIZE,
  PDF_CONFIGURATION_TEXT_OVERFLOW,
} from '../../constants';
import { AttachmentConfiguration, PdfFormat } from '../../types/attachment';
import OutsideClickDetector from './OutsideClickDetector';
import { PRINT_PREVIEW_FONT_SLIDER_CONFIG } from 'common/components/PdfPreviewModal/templates/section/defaultStyles';
import { ScheduleSendValues } from '../../types/scheduleSend';
import { I18n } from 'common/i18n';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';

const scope = 'shared.components.schedule_send_report.modal.attachment_section.pdf_configuration';

const PDF_DEFAULTS = {
  PAGE_SIZE: 'Letter',
  ORIENTATION: 'landscape',
  COLUMN_WIDTH: 'bestfit',
  TEXT_OVERFLOW: 'linebreak',
  PAGE_BREAK: 'standard',
} as const;

const COLUMN_WIDTH_INHERIT = 'inherit';
const FORMAT_PDF = 'pdf';
const TABLE_FULL_REPORT = 'full-report';

const getPdfOptionsWithDefaults = (pdfConfigValues: PdfFormat | undefined) => ({
  pageSize: pdfConfigValues?.pageSize ?? PDF_DEFAULTS.PAGE_SIZE,
  orientation: pdfConfigValues?.orientation ?? PDF_DEFAULTS.ORIENTATION,
  columnWidth: pdfConfigValues?.columnWidth ?? PDF_DEFAULTS.COLUMN_WIDTH,
  textOverflow: pdfConfigValues?.textOverflow ?? PDF_DEFAULTS.TEXT_OVERFLOW,
  pageBreak: pdfConfigValues?.pageBreak ?? PDF_DEFAULTS.PAGE_BREAK,
  fontSize: pdfConfigValues?.fontSize ?? PRINT_PREVIEW_FONT_SLIDER_CONFIG.DEFAULT,
});

export interface PdfConfigurationProps {
  index: number;
  tableCurrentValue: string;
  formatCurrentValue: string;
  attachmentConfigs: AttachmentConfiguration[];
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
  isTableWithGrouping: boolean;
}

const PdfConfiguration: FC<PdfConfigurationProps> = (props) => {
  const {
    index,
    tableCurrentValue,
    formatCurrentValue,
    attachmentConfigs,
    updateScheduleSendValue,
    isTableWithGrouping,
  } = props;

  const pdfConfigValues = attachmentConfigs[index].pdfOptions;
  const pdfOptionsSavedValues = useMemo(() => getPdfOptionsWithDefaults(pdfConfigValues), [pdfConfigValues]);
  const [pageSize, setPageSize] = useState<string>(pdfOptionsSavedValues.pageSize);
  const [orientation, setOrientation] = useState<string>(pdfOptionsSavedValues.orientation);
  const [columnWidth, setColumnWidth] = useState<string>(pdfOptionsSavedValues.columnWidth);
  const [textOverflow, setTextOverflow] = useState<string>(pdfOptionsSavedValues.textOverflow);
  const [pageBreak, setPageBreak] = useState<string>(pdfOptionsSavedValues.pageBreak);
  const [fontSize, setFontSize] = useState<number>(pdfOptionsSavedValues.fontSize);
  const [flyoutOpen, setFlyoutOpen] = useState<boolean>(false);

  const handleSelectChange = (setter: (value: string) => void) => (event: CustomEvent) => {
    const value = event.detail as string;
    setter(value);
  };

  const onPageSizeChange = handleSelectChange(setPageSize);
  const onOrientationChange = handleSelectChange(setOrientation);
  const onColumnWidthChange = handleSelectChange(setColumnWidth);
  const onTextOverflowChange = handleSelectChange(setTextOverflow);
  const onPageBreakChange = handleSelectChange(setPageBreak);

  const onFontSizeChange = (event: CustomEvent) => {
    const sliderValue = event.detail.value as number;
    setFontSize(sliderValue);
  };

  const closePopover = () => {
    setFlyoutOpen(false);
  };

  function onSavePdfConfig() {
    const pdfOptions = {
      pageSize,
      orientation,
      columnWidth,
      textOverflow,
      ...(isTableWithGrouping && { pageBreak }),
      ...(columnWidth === COLUMN_WIDTH_INHERIT && { fontSize }),
    };

    attachmentConfigs[index].pdfOptions = pdfOptions;
    updateScheduleSendValue('attachmentConfigs', attachmentConfigs);
    closePopover();
  }

  const resetStates = () => {
    setPageSize(pdfOptionsSavedValues.pageSize);
    setOrientation(pdfOptionsSavedValues.orientation);
    setColumnWidth(pdfOptionsSavedValues.columnWidth);
    setTextOverflow(pdfOptionsSavedValues.textOverflow);
    setPageBreak(pdfOptionsSavedValues.pageBreak);
    setFontSize(pdfOptionsSavedValues.fontSize);
  };

  const handleCancel = () => {
    resetStates();
    closePopover();
  };

  useEscapeKey({
    onKeydown: handleCancel,
    enabled: flyoutOpen,
    eventTarget: document,
  });

  return (
    <OutsideClickDetector onOutsideClick={handleCancel}>
      <div>
        <ForgeIconButton
          densityLevel={3}
          slot="trailing"
          className="pdf-options-btn"
          data-testid="pdf-settings-btn"
          id={`pdf-options-${index}`}
          hidden={formatCurrentValue !== FORMAT_PDF || tableCurrentValue === TABLE_FULL_REPORT}
          dense
          onClick={() => {
            setFlyoutOpen(true);
          }}
        >
          <ForgeIcon slot="end" name="settings" />
        </ForgeIconButton>

        {flyoutOpen && (
          <ForgePopover
            data-testid="pdf-config-popover"
            anchor={`pdf-options-${index}`}
            placement={'right'}
            open
          >
            <div className="pdf-popover">
              <div className="forge-typography--body2 pdf-configurations-header">
                {I18n.t('title', { scope })}
              </div>
              <ForgeSelect
                className="pdf-config-select"
                data-testid="page-size-select"
                label={I18n.t('page_size', { scope })}
                labelPosition="block-start"
                value={pageSize}
                options={PDF_CONFIGURATION_PAGE_SIZE}
                syncPopupWidth
                on-change={(event: CustomEvent) => onPageSizeChange(event)}
              />

              <ForgeSelect
                className="pdf-config-select"
                data-testid="orientation-select"
                label={I18n.t('orientation', { scope })}
                labelPosition="block-start"
                value={orientation}
                options={PDF_CONFIGURATION_ORIENTATION}
                syncPopupWidth
                on-change={(event: CustomEvent) => onOrientationChange(event)}
              />

              <ForgeSelect
                className="pdf-config-select"
                data-testid="column-width-select"
                label={I18n.t('column_width', { scope })}
                labelPosition="block-start"
                value={columnWidth}
                options={PDF_CONFIGURATION_COLUMN_WIDTH}
                syncPopupWidth
                on-change={(event: CustomEvent) => onColumnWidthChange(event)}
              />

              {columnWidth === COLUMN_WIDTH_INHERIT && (
                <div data-testid="font-size-slider-container">
                  <ForgeLabelValue id="font-size-slider" className="label">
                    <span slot="label" id="font-size-slider-label">
                      {I18n.t('font_size', { scope })}
                    </span>
                  </ForgeLabelValue>
                  <ForgeSlider
                    role="forge-slider"
                    style={{ width: '100%' }}
                    type="continuous"
                    min={PRINT_PREVIEW_FONT_SLIDER_CONFIG.MIN}
                    max={PRINT_PREVIEW_FONT_SLIDER_CONFIG.MAX}
                    value={fontSize}
                    step={PRINT_PREVIEW_FONT_SLIDER_CONFIG.STEP}
                    on-forge-slider-change={(event: CustomEvent) => onFontSizeChange(event)}
                  />
                </div>
              )}

              <ForgeSelect
                className="pdf-config-select"
                data-testid="text-overflow-select"
                label={I18n.t('text_overflow', { scope })}
                labelPosition="block-start"
                value={textOverflow}
                options={PDF_CONFIGURATION_TEXT_OVERFLOW}
                syncPopupWidth
                on-change={(event: CustomEvent) => onTextOverflowChange(event)}
              />

              {isTableWithGrouping && (
                <ForgeSelect
                  className="pdf-config-select"
                  data-testid="page-break-select"
                  label={I18n.t('page_break', { scope })}
                  labelPosition="block-start"
                  value={pageBreak}
                  options={PDF_CONFIGURATION_PAGE_BREAK}
                  syncPopupWidth
                  on-change={(event: CustomEvent) => onPageBreakChange(event)}
                />
              )}

              <ForgeButton
                data-testid="save-pdf-config-btn"
                className="save-pdf-config"
                variant="raised"
                onClick={onSavePdfConfig}
              >
                <span>{I18n.t('save', { scope })}</span>
              </ForgeButton>
            </div>
          </ForgePopover>
        )}
      </div>
    </OutsideClickDetector>
  );
};

export default PdfConfiguration;
